import React, { useState } from 'react';
import AMPageShell from './components/AMPageShell';
import Icon from 'components/AppIcon';
import { AUDIT_LOG } from './data/assetData';

const ACTION_COLOURS = {
  'Review created': 'bg-primary/10 text-primary',
  'Submitted deadline met': 'bg-success/10 text-success',
  'Oversight decision recorded': 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  'Actions issued': 'bg-warning/10 text-warning',
  'Moved to watchlist': 'bg-orange-100 text-orange-700',
  'Escalated': 'bg-error/10 text-error',
  'Exit recommendation': 'bg-accent/20 text-accent-foreground',
  'Actions closed': 'bg-success/10 text-success',
};

export default function AuditTrailExports({ selectedAssets, showToast }) {
  const [log] = useState(AUDIT_LOG);
  const [filterAction, setFilterAction] = useState('');
  const [filterAsset, setFilterAsset] = useState('');

  const actions = [...new Set(log.map((e) => e.action))];
  const assetNames = [...new Set(log.map((e) => e.asset))];

  const filtered = log?.filter((e) => {
    if (filterAction && e?.action !== filterAction) return false;
    if (filterAsset && e?.asset !== filterAsset) return false;
    return true;
  });

  const handleExport = (type) => {
    showToast?.(`Exporting ${type}… (demo — no file generated)`);
  };

  return (
    <AMPageShell activeTab="/asset-management/audit-trail-exports">
      <div className="space-y-6">
        {/* Export buttons */}
        <div className="bg-card border border-border rounded-lg shadow-xs p-5">
          <h2 className="font-heading font-semibold text-foreground mb-4" style={{ fontSize: '1.1rem' }}>Exports</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Export Review Pack (PDF preview)', icon: 'FileText', type: 'Review Pack PDF' },
              { label: 'Export Portfolio Oversight Summary', icon: 'BarChart2', type: 'Portfolio Oversight Summary' },
              { label: 'Export Value Creation Register', icon: 'TrendingUp', type: 'Value Creation Register' },
            ]?.map((btn) => (
              <button
                key={btn?.label}
                onClick={() => handleExport(btn?.type)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-md text-sm font-caption font-medium hover:bg-primary/90 transition-all duration-250 shadow-xs"
              >
                <Icon name={btn?.icon} size={15} color="white" strokeWidth={2} />
                {btn?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Audit log */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={15} color="var(--color-muted-foreground)" strokeWidth={2} />
              <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Audit Trail</h2>
              <span className="font-caption text-xs text-muted-foreground">(immutable log)</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <select value={filterAction} onChange={(e) => setFilterAction(e?.target?.value)}
                className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground font-caption">
                <option value="">All actions</option>
                {actions?.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={filterAsset} onChange={(e) => setFilterAsset(e?.target?.value)}
                className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground font-caption">
                <option value="">All assets</option>
                {assetNames?.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Timestamp', 'User', 'Action', 'Asset', 'Detail']?.map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((entry) => (
                  <tr key={entry?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-data text-xs text-muted-foreground whitespace-nowrap">{entry?.timestamp}</td>
                    <td className="px-4 py-3 font-caption text-sm text-foreground">{entry?.user}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium whitespace-nowrap ${ACTION_COLOURS?.[entry?.action] || 'bg-muted text-muted-foreground'}`}>
                        {entry?.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-caption text-sm text-foreground">{entry?.asset}</td>
                    <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{entry?.detail}</td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center font-caption text-sm text-muted-foreground">No entries match filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
